import React, { useState, useEffect } from 'react';
import {
  Form,
  Select,
  DatePicker,
  Button,
  Card,
  Space,
  Input,
  Empty,
  Row,
} from 'antd';
import moment from 'moment';
import { Column } from '@ant-design/plots';
import { LeftOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import openNotification from '../../components/Toastr';
import { getErrorMessage } from '../../api/api';
import reviewsAPI from '../../api/reviews';
import partnerApi from '../../api/partner';

function ReviewRequestsChart() {
  const { t } = useTranslation();
  const session = useSelector((store) => store.Session.session);
  const history = useHistory();

  const [form] = Form.useForm();
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [personalizedButton, setPersonalizedButton] = useState(false);
  const [filterMarketplaces, setFilterMarketplaces] = useState([]);

  const getMarketplacesList = async () => {
    await partnerApi
      .getMarketplacesAllowed(
        session?.userInfo?.commercial_partner_id[0],
        'Seller'
      )
      .then((result) => {
        const mkts = result?.data
          ?.filter((marketplace) => marketplace.includes('Amazon'))
          .map((marketplace) => {
            return {
              value: marketplace,
              label: `${t(
                'dashboard.marketplaces.'.concat(marketplace.replace(' ', ''))
              )}`,
            };
          });
        setFilterMarketplaces(mkts);
        if (mkts[0]?.value) {
          form.setFieldValue('marketplace', mkts[0]?.value);
        }
      })
      .catch(() => {
        setFilterMarketplaces([]);
      });
  };

  const handlePersonalizedButton = (period) => {
    let dateRange;
    let groupBy = 'DAILY';
    switch (period) {
      case 'custom':
        break;
      case 'currentWeek':
        dateRange = [moment().add(-moment().weekday(), 'd'), moment()];
        break;
      case 'last7days':
        dateRange = [moment().add(-6, 'd'), moment()];
        break;
      case 'currentMonth':
        dateRange = [moment().startOf('month'), moment()];
        break;
      case 'lastMonth':
        dateRange = [
          moment().subtract(1, 'months').startOf('month'),
          moment().subtract(1, 'months').endOf('month'),
        ];
        break;
      case 'last30days':
        dateRange = [moment().add(-30, 'd'), moment()];
        break;
      case 'currentYear':
        dateRange = [moment().startOf('year'), moment()];
        groupBy = 'MONTHLY';
        break;
      case 'last12months':
        dateRange = [moment().add(-12, 'M'), moment()];
        groupBy = 'MONTHLY';
        break;
      default:
        break;
    }

    setPersonalizedButton(period === 'custom');
    form.setFieldsValue({
      dateFrom: dateRange?.[0],
      dateTo: dateRange?.[1],
      groupBy,
    });
  };

  const onDateRangeChange = (dates) => {
    if (dates?.length === 2) {
      const [start, end] = dates;
      end.set({ hour: 23, minute: 59, second: 59 });
      form.setFieldsValue({ dateFrom: start, dateTo: end });
    }
  };

  const getReviewRequests = async (values) => {
    const { marketplace, groupBy, dateFrom, dateTo, sku } = values;

    setLoading(true);
    try {
      const res = await reviewsAPI.getReviewRequests({
        marketplace,
        groupBy,
        dateFrom: dateFrom?.format('YYYY-MM-DD'),
        dateTo: dateTo?.format('YYYY-MM-DD'),
        ...(sku ? { sku } : {}),
      });
      setChartData(res.data.results);
    } catch (error) {
      openNotification({ status: false, content: getErrorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    await getReviewRequests(values);
  };

  useEffect(() => {
    const fetchData = async () => {
      await getMarketplacesList();
      handlePersonalizedButton('last7days');
      form.submit();
    };
    fetchData();
  }, []);

  return (
    <div className="content-div container-products">
      <Space direction="vertical" style={{ display: 'flex' }}>
        <Card
          bordered={false}
          title={
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              {t('catalogue.reviewRequests')}
              <Button
                className="btn-basic"
                icon={<LeftOutlined />}
                onClick={() => history.push('/catalogo/reviews')}
              >
                {t('catalogue.backToReviews')}
              </Button>
            </div>
          }
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Row style={{ width: '100%' }}>
              <Form.Item
                name="marketplace"
                label="Marketplace"
                rules={[{ required: true, message: t('common.required') }]}
              >
                <Select
                  style={{ width: 150, marginRight: 20 }}
                  options={filterMarketplaces}
                />
              </Form.Item>

              <Form.Item name="sku" label="SKU">
                <Input style={{ width: 150, marginRight: 20 }} />
              </Form.Item>

              <Form.Item
                name="period"
                initialValue="currentMonth"
                label={t('dashboard.fields.period')}
                rules={[{ required: true, message: t('common.required') }]}
              >
                <Select
                  style={{ width: 150, marginRight: 20 }}
                  defaultValue="currentMonth"
                  onChange={handlePersonalizedButton}
                  options={[
                    {
                      value: 'currentWeek',
                      label: t('dashboard.filter.currentWeek'),
                    },
                    {
                      value: 'last7days',
                      label: t('dashboard.filter.last7days'),
                    },
                    {
                      value: 'currentMonth',
                      label: t('dashboard.filter.currentMonth'),
                    },
                    {
                      value: 'lastMonth',
                      label: t('dashboard.filter.lastMonth'),
                    },
                    {
                      value: 'last30days',
                      label: t('dashboard.filter.last30days'),
                    },
                    {
                      value: 'currentYear',
                      label: t('dashboard.filter.currentYear'),
                    },
                    {
                      value: 'last12months',
                      label: t('dashboard.filter.last12months'),
                    },
                    { value: 'custom', label: t('dashboard.filter.custom') },
                  ]}
                />
              </Form.Item>

              <Form.Item name="rangePicker" label=" ">
                <DatePicker.RangePicker
                  disabled={!personalizedButton}
                  onChange={onDateRangeChange}
                  style={{ width: 250 }}
                  disabledDate={(d) => !d || d.isAfter(moment())}
                  placeholder={[
                    t('dashboard.fields.startDate'),
                    t('dashboard.fields.endDate'),
                  ]}
                />
              </Form.Item>

              {['groupBy', 'dateFrom', 'dateTo'].map((field) => (
                <Form.Item
                  hidden
                  noStyle
                  name={field}
                  rules={[{ required: true, message: t('common.required') }]}
                />
              ))}

              <Form.Item label=" ">
                <Button
                  style={{ padding: '0 25px', marginLeft: 20 }}
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                >
                  {t('common.filter')}
                </Button>
              </Form.Item>
            </Row>
          </Form>
        </Card>
        <Card>
          {chartData && chartData.length > 0 ? (
            <Column
              data={chartData}
              xField="period"
              yField="reviewRequests"
              columnStyle={{ radius: [4, 4, 0, 0] }}
              tooltip={{ showMarkers: false }}
              meta={{
                period: { alias: t('common.date') },
                reviewRequests: { alias: t('catalogue.reviewRequests') },
              }}
              loading={loading}
            />
          ) : (
            <Empty description={t('common.noData')} />
          )}
        </Card>
      </Space>
    </div>
  );
}

export default ReviewRequestsChart;
